/**
 * Where a seat lands (MAR-1172): `billing.plans.start_page`, edited in Super Admin → Planes.
 *
 * `chat` is the default and the only value every plan had before the setting existed, so a plan
 * nobody touched behaves exactly as it always did. The other two send a seat to the workflow runner
 * or the law reader after login, and away from the `/app` chat home when that plan cannot chat.
 *
 * Pure, shared by `login.vue`, the app shell and the admin editor, so the routing rule is written once.
 */
export const START_PAGES = ['chat', 'workflows', 'laws'] as const
export type StartPage = typeof START_PAGES[number]

/** The page each start page names. */
export const START_PAGE_PATH: Record<StartPage, string> = {
  chat: '/app',
  workflows: '/app/workflows',
  laws: '/app/laws',
}

export function isStartPage(value: unknown): value is StartPage {
  return typeof value === 'string' && (START_PAGES as readonly string[]).includes(value)
}

/** The page to land on after login; anything missing or unknown is the chat home. */
export function postLoginPath(startPage: unknown): string {
  return isStartPage(startPage) ? START_PAGE_PATH[startPage] : START_PAGE_PATH.chat
}

/** What the redirect rule reads of the profile. */
export interface HomeRedirectProfile {
  start_page?: string | null
  chat_limit?: number | null
  feature_flags?: { chat?: boolean } | null
}

/**
 * Where the `/app` chat home (the Marcus logo, a typed address) must send this seat instead, or
 * `null` to leave it alone. Only a plan whose start page is NOT chat is ever moved, and only while its
 * chat is unusable: the allowance is 0 or the plan has chat switched off. A plan that can chat keeps
 * the chat home even if its start page is the workflow runner.
 */
export function homeRedirectFor(path: string, profile: HomeRedirectProfile | null | undefined): string | null {
  if (path !== '/app' && path !== '/app/') return null
  if (!profile || !isStartPage(profile.start_page) || profile.start_page === 'chat') return null
  const chatUnusable = profile.chat_limit === 0 || profile.feature_flags?.chat === false
  return chatUnusable ? START_PAGE_PATH[profile.start_page] : null
}
